// After a deploy, clients with a cached index.html reference chunk files that
// no longer exist; the request falls back to index.html and fails with a
// ChunkLoadError or SyntaxError ("Unexpected token '<'").

export const CHUNK_RELOAD_GUARD_KEY = 'chunk-load-error-reloaded';

// One recovery reload per window. Clearing the guard on successful navigation
// re-armed recovery for chunks that fail after navigating (async widgets,
// prefetches), looping reloads on a broken deploy with every failure
// suppressed; re-arming by time keeps repeat failures visible to Sentry while
// still letting the next stale deploy recover.
const RECOVERY_WINDOW_MS = 60 * 1000;

// The recovery reload wipes module state while the sessionStorage guard
// persists: a fresh guard without this flag means recovery already ran on a
// previous page load and did not heal the chunk failure.
let recoveryTriggeredByThisPageLoad = false;

function readRecoveryStartedAt() {
    try {
        return Number(sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY));
    } catch {
        return 0;
    }
}

function recoveryIsFresh() {
    const startedAt = readRecoveryStartedAt();

    return startedAt > 0 && Date.now() - startedAt < RECOVERY_WINDOW_MS;
}

export function beginChunkRecovery() {
    if (recoveryIsFresh()) {
        return false;
    }

    try {
        sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
        return false;
    }

    recoveryTriggeredByThisPageLoad = true;

    return true;
}

export function chunkRecoveryFailed() {
    return !recoveryTriggeredByThisPageLoad && recoveryIsFresh();
}

// A hashed build asset, e.g. /js/4967.7f38e083.js or /css/123.abcdef0.css.
const CHUNK_ASSET_RE = /\.[0-9a-f]{6,}\.(js|css)([?#].*)?$/i;

export function isChunkAssetUrl(url) {
    return typeof url === 'string' && CHUNK_ASSET_RE.test(url);
}

const CHUNK_PARSE_MESSAGES = ["Unexpected token '<'", "expected expression, got '<'", 'Unexpected end of script'];

export function isChunkParseError(message) {
    return typeof message === 'string'
        && CHUNK_PARSE_MESSAGES.some((needle) => message.includes(needle));
}

export function isChunkLoadError(error) {
    if (!error) {
        return false;
    }

    const name = error.name || '';
    const message = error.message || '';

    return name === 'ChunkLoadError'
        || /Loading( CSS)? chunk \S+ failed/i.test(message)
        || (name === 'SyntaxError' && isChunkParseError(message))
        || /(error loading|failed to fetch) dynamically imported module/i.test(message)
        || /importing a module script failed/i.test(message);
}

export function isStaleChunkEvent(event) {
    const values = event?.exception?.values ?? [];

    return values.some((value) => {
        const type = value?.type ?? '';
        const message = value?.value ?? '';

        // Genuine app SyntaxErrors share this message; only frames that all
        // point at build assets mark it as a stale chunk.
        if (type === 'SyntaxError' && isChunkParseError(message)) {
            const frames = value?.stacktrace?.frames ?? [];

            return frames.length > 0 && frames.every((frame) => isChunkAssetUrl(frame?.filename ?? ''));
        }

        return isChunkLoadError({ name: type, message });
    });
}
